import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import * as StepsService from '../services/StepsService';
import { Step } from '../types';
import { notifyError, notifySuccess } from '../../../components/PlatformNotification';

const DEFAULT_LIMIT = 10;

// Query keys
export const stepsKeys = {
    all: ['steps'] as const,
    lists: () => [...stepsKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) => [...stepsKeys.lists(), { filters }] as const,
    details: () => [...stepsKeys.all, 'detail'] as const,
    detail: (id: string) => [...stepsKeys.details(), id] as const,
};

// Hooks
//
// Token (cursor) pagination — the platform standard. `filters` carries the
// PARENT ROUTE ID (phaseId, or playbookId as a fallback) built by the table
// from useParams, and `stepsKeys.list` puts it in the queryKey: omit it and
// navigating from one phase to another silently serves the previous phase's
// cached steps.
export const useStepsQuery = (filters: Record<string, string> = {}) => {
    return useInfiniteQuery({
        queryKey: stepsKeys.list(filters),
        queryFn: ({ pageParam }: { pageParam: string | null }) =>
            StepsService.fetchRecords({
                filter: filters,
                limit: DEFAULT_LIMIT,
                nextToken: pageParam,
            }),
        // MUST be undefined, not null and never the same token again — either
        // of those makes React Query refetch forever.
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
    });
};

export const useStepQuery = (id: string) => {
    return useQuery({
        queryKey: stepsKeys.detail(id),
        queryFn: () => StepsService.fetch(id),
        enabled: !!id,
    });
};

export const useCreateStepMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: StepsService.add,
        onSuccess: () => {
            notifySuccess('Step created');
            queryClient.invalidateQueries({ queryKey: stepsKeys.lists() });
        },
        onError: notifyError,
    });
};

export const useUpdateStepMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, record }: { id: string; record: Step }) =>
            StepsService.update(id, record),
        onSuccess: (_, { id }) => {
            notifySuccess('Step updated');
            queryClient.invalidateQueries({ queryKey: stepsKeys.lists() });
            queryClient.invalidateQueries({ queryKey: stepsKeys.detail(id) });
        },
        onError: notifyError,
    });
};

export const useDeleteStepMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: StepsService.deleteRecord,
        onSuccess: () => {
            notifySuccess('Step deleted');
            queryClient.invalidateQueries({ queryKey: stepsKeys.all });
        },
        onError: notifyError,
    });
};

// Global filter state (shared across components)
let globalFilters: Record<string, string> = {};
let filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const useStepsFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => {
            setFilters(newFilters);
        };
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return {
        filters,
        handleSetFilters,
        handleClearFilters,
    };
};
